import type { BulkExportResponse, ReassignResult } from '../api/types';

export interface ExportStats {
  created: number;
  alreadyQueued: number;
  removedEarlier: number;
  alreadyLogged: number;
  alreadyClipping: number;
}

/**
 * Why each sent lead did or didn't land in the queue. An existing prospect's
 * status says why it wasn't re-added: still open, removed by an outreacher
 * earlier, or already logged (PROMOTED). A live invite also counts as logged.
 */
export function exportStats(response: BulkExportResponse): ExportStats {
  const s: ExportStats = { created: 0, alreadyQueued: 0, removedEarlier: 0, alreadyLogged: 0, alreadyClipping: 0 };
  for (const r of response.results ?? []) {
    const o = r.outcome;
    if (o.status === 'created') s.created++;
    else if (o.status === 'already-prospect') {
      // Widened: the backend also sends CONTACTED, which ProspectStatus predates.
      const existing: string = o.existingStatus;
      if (existing === 'DISMISSED') s.removedEarlier++;
      else if (existing === 'PROMOTED') s.alreadyLogged++;
      else s.alreadyQueued++;
    } else if (o.status === 'already-invited') s.alreadyLogged++;
    else if (o.status === 'already-clipping-member') s.alreadyClipping++;
  }
  return s;
}

export function exportSummaryText(response: BulkExportResponse): { title: string; detail: string | null } {
  const s = exportStats(response);
  const parts = [
    s.alreadyQueued && `${s.alreadyQueued} already in the lead queue`,
    s.removedEarlier && `${s.removedEarlier} removed from the queue earlier`,
    s.alreadyLogged && `${s.alreadyLogged} already logged`,
    s.alreadyClipping && `${s.alreadyClipping} already on the platform`,
  ].filter(Boolean) as string[];
  return {
    title: `${s.created} added to the lead queue`,
    detail: parts.length ? `${parts.join(', ')}.` : null,
  };
}

export function reassignSummaryText(r: ReassignResult): { title: string; detail: string | null } {
  const parts = [
    r.skipped && `${r.skipped} skipped (already logged or closed)`,
    r.notInOutreach && `${r.notInOutreach} not in outreach yet — send them first`,
  ].filter(Boolean) as string[];
  return {
    title: `Reassigned ${r.updated} lead${r.updated === 1 ? '' : 's'}`,
    detail: parts.length ? `${parts.join('. ')}.` : null,
  };
}
